import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import { RouteComponentProps, StaticContext } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { LiveOrderScreen } from '../OrderScreen';
import OrdersList from './containers/OrdersList/OrdersList';
import OrdersTabs from './containers/OrdersTabs/OrdersTabs';
import { SCREENS } from 'src/constants/screens';
import { localeStore } from 'src/mobx/localesStore';
import { userStore } from 'src/mobx/userStore';
import AdvancedFilter from 'src/view/components/AdvancedFilter/AdvancedFilter';
import { Table } from 'src/view/components/Table/Table';
import Headers from 'src/view/containers/Headers/Headers';
import HeaderTitle from 'src/view/containers/Headers/components/HeaderTitle/HeaderTitle';
import { IBranchesModalItem } from 'src/view/containers/Modals/BranchesModal/BranchesModal';
import BranchesModalOpen from 'src/view/containers/Modals/BranchesModal/components/BranchesModalOpen/BranchesModalOpen';
import { useLiveOrdersContext } from '~/mobx/ordersStore/context';
import { liveOrdersStore } from '~/mobx/ordersStore/liveOrdersStore';
import { history } from '~/services/history';
import { LoadingStatus } from '~/types/main';
import { TOrder } from '~/types/orders';
import getOrderViewPath from '~/view/screens/CheckoutScreen/getOrderViewPath';

type TProps = RouteComponentProps<{ id?: string }, StaticContext, undefined>;

const LiveOrdersScreen: React.FC<TProps> = ({ match }) => {
  const { store } = useLiveOrdersContext();

  useEffect(() => {
    if (store.tabCountersStatus === LoadingStatus.Init) {
      liveOrdersStore.fetchInitCounters();
    }
  }, []);

  const branchesSelect = useCallback((branches: IBranchesModalItem[]) => {
    store.selectBranches(branches);
  }, []);

  const handleSelectOrder = useCallback(
    (order: TOrder) => {
      if (!order.viewed) {
        liveOrdersStore.markOrderViewed(order.id);
      }
      if (match.params.id !== order.id) {
        history.push(getOrderViewPath(liveOrdersStore, order.id), toJS(order));
      }
    },
    [match.params]
  );

  return (
    <div className="d-flex flex-column full-width">
      <Headers>
        <div className="d-flex flex-row align-items-center justify-content-between">
          <HeaderTitle title="live_orders" />
          <div className="d-flex flex-row align-content-end align-items-center">
            <BranchesModalOpen
              selectedBranches={store.selectedBranches.length ? store.selectedBranches : userStore.user.branches}
              onSave={branchesSelect}
              highlightUnavailable={false}
              isMulti
              busy
            />
            <AdvancedFilter store={store.advancedFilter} />
          </div>
        </div>
      </Headers>
      <div className="d-flex flex-column flex-grow-1 pt-6">
        <div className="d-flex flex-row justify-content-between pr-4">
          <OrdersTabs />
        </div>
        <Table>
          <div className="d-flex flex-row flex-grow-1 pt-4">
            <OrdersList enableBlink onSelectOrder={handleSelectOrder} store={liveOrdersStore} />
            <Switch>
              <Route
                exact
                path={[
                  SCREENS.LIVE_ORDER(':id'),
                  SCREENS.LIVE_ORDER_LOGS(':id'),
                  SCREENS.LIVE_ORDER_TECHNICAL_LOGS(':id'),
                  SCREENS.LIVE_ORDER_CONTENT_LOGS(':id'),
                  SCREENS.LIVE_ORDER_TRANSACTION_LOGS(':id'),
                ]}
                component={LiveOrderScreen}
              />
              <Route exact path={SCREENS.LIVE_ORDERS()}>
                <h2 className="family-medium text-size-24 color-dark color-gray2 mx-auto my-auto">{localeStore.t('order_not_selected')}</h2>
              </Route>
            </Switch>
          </div>
        </Table>
      </div>
    </div>
  );
};

export default observer(LiveOrdersScreen);
